import { IconNames } from '@blueprintjs/icons';
import { CONTRACT_RATE, ReferentPriceTypes } from '@strata/constants';
import { ActionButtonText } from '@strata/react-buttons';
import { DialogWithForm } from '@strata/react-dialog';
import { FileUploadFormik } from '@strata/react-file-upload';
import { InlineMessage } from '@strata/react-inline-message';
import { SuccessToast } from '@strata/react-toast-message';
import { validationStrings } from '@strata/strata-ui-strings';
import { saveAs } from 'file-saver';
import { Formik, useFormikContext } from 'formik';
import Papa from 'papaparse';
import PropTypes from 'prop-types';
import * as Yup from 'yup';

import { handleError } from 'services/ErrorManagement';
import { useGet } from 'services/Http';
import {
    filterListItems,
    ListSchema,
    ListType,
    ListTypeItemKeyMap,
} from 'services/ListManagementHelpers';

/** CSV dolumn headers */
const Col = {
    OID: 'OID',
    DRUG_VERSION: 'Brand/Generic',
    REFERENT: 'Referent/Contract',
    OFFER_RATE: 'Offer Rate',
};

/** CSV data for the example template */
const productListBulkTemplateCSVMap = {
    [ListSchema.assignment]: [
        [
            `${Col.OID},${Col.DRUG_VERSION},${Col.REFERENT},${Col.OFFER_RATE}`,
            'remicade.med,Brand,AWP,-0.203',
            'remicade.med,Generic,ASP,0.1034',
            'inflectra.med,Brand,,',
            'inflectra.med,Generic,Contract,',
        ].join('\n'),
    ],
    [ListSchema.finance]: [
        [
            `${Col.OID},${Col.DRUG_VERSION}`,
            'remicade.med,Brand',
            'remicade.med,Generic',
            'inflectra.med,Brand',
            'inflectra.med,Generic',
        ].join('\n'),
    ],
};

const invalidMsg = (row, col) => `Invalid ${col}: '${row[col]}'`;

const validateRow = (row) => {
    const rowErrors = [];

    if (
        !row[Col.DRUG_VERSION] ||
        !['brand', 'generic'].includes(row[Col.DRUG_VERSION].toLowerCase())
    ) {
        rowErrors.push(invalidMsg(row, Col.DRUG_VERSION));
    }

    // Invalid Referent
    if (
        !!row[Col.REFERENT] &&
        ![ReferentPriceTypes.AWP, ReferentPriceTypes.ASP, 'CONTRACT'].includes(
            row[Col.REFERENT].toUpperCase(),
        )
    ) {
        rowErrors.push(invalidMsg(row, Col.REFERENT));
    }

    // Offer Rate without Referent
    if (!!row[Col.OFFER_RATE] && !row[Col.REFERENT]) {
        rowErrors.push(invalidMsg(row, Col.REFERENT));
    }

    // Non-Contract referent without offer rate
    if (
        !!row[Col.REFERENT] &&
        row[Col.REFERENT].toLowerCase() !== 'contract' &&
        !row[Col.OFFER_RATE]
    ) {
        rowErrors.push(invalidMsg(row, Col.OFFER_RATE));
    }

    // Invalid Offer Rate
    if (!!row[Col.OFFER_RATE] && (row[Col.OFFER_RATE] > 1 || row[Col.OFFER_RATE] < -1)) {
        rowErrors.push(invalidMsg(row, Col.OFFER_RATE));
    }

    return rowErrors;
};

const processFile = (results, products) => {
    const productsToAdd = [];
    const validationErrors = {};
    results.data.forEach((row, i) => {
        const rowErrors = [];

        const matchingProduct = products.find((product) => product.oid === row[Col.OID]);

        if (!matchingProduct) {
            rowErrors.push(invalidMsg(row, Col.OID));
        }

        rowErrors.push(...validateRow(row));

        if (rowErrors.length === 0) {
            const isBrand = row[Col.DRUG_VERSION].toLowerCase() === 'brand';
            const productToAdd = {
                [ListTypeItemKeyMap[ListType.product][0]]: isBrand
                    ? matchingProduct.brand_product_description
                    : matchingProduct.generic_product_description,
                [ListTypeItemKeyMap[ListType.product][1]]: row[Col.OID],
                [ListTypeItemKeyMap[ListType.product][2]]:
                    row[Col.REFERENT]?.toLowerCase() !== 'contract'
                        ? (row[Col.OFFER_RATE] ?? undefined)
                        : CONTRACT_RATE,
                referent_price_type:
                    row[Col.REFERENT]?.toLowerCase() !== 'contract'
                        ? row[Col.REFERENT]?.toUpperCase()
                        : undefined,
                is_brand: isBrand,
            };

            const isDuplicate = productsToAdd.some(
                (product) =>
                    product[ListTypeItemKeyMap[ListType.product][1]] ===
                        productToAdd[ListTypeItemKeyMap[ListType.product][1]] &&
                    product.is_brand === productToAdd.is_brand,
            );

            if (!isDuplicate) {
                productsToAdd.push(productToAdd);
            }
        } else {
            validationErrors[i + 2] = rowErrors;
        }
    });

    return { productsToAdd, validationErrors };
};

export const ProductsBulkUploadDialog = ({ isOpen, onClose, listSchema }) => {
    const listFormik = useFormikContext();
    const products = useGet('/reference/product_benefit_configs/');

    return (
        <Formik
            initialValues={{}}
            enableReinitialize
            validationSchema={Yup.object({
                productFile: Yup.array()
                    .required(validationStrings.requiredField.errorMessage)
                    .length(1, validationStrings.requiredField.errorMessage),
            })}
            onSubmit={async (values, { setFieldValue, resetForm }) => {
                Papa.parse(values.fileToUpload, {
                    header: true,
                    dynamicTyping: true,
                    complete: async (results) => {
                        const { productsToAdd, validationErrors } = processFile(results, products);

                        if (Object.keys(validationErrors).length > 0) {
                            handleError('Invalid values found in file.');
                            setFieldValue('validationErrors', validationErrors);
                        } else {
                            await listFormik.setValues({
                                ...listFormik.values,
                                listItems: [
                                    ...filterListItems(
                                        listFormik.values.listItems ?? [],
                                        productsToAdd,
                                        ListType.product,
                                    ),
                                    ...productsToAdd,
                                ],
                                addedItems: [
                                    ...listFormik.values.addedItems,
                                    ...filterListItems(
                                        productsToAdd,
                                        listFormik.values.addedItems,
                                        ListType.product,
                                    ),
                                ],
                                deletedItems: filterListItems(
                                    listFormik.values.deletedItems,
                                    productsToAdd,
                                    ListType.product,
                                ),
                            });
                            SuccessToast({ message: 'Products added successfully.' });
                            resetForm();
                            onClose();
                        }
                    },
                    error: (error) => handleError(error.message, error),
                });
            }}
        >
            {(formik) => (
                <DialogWithForm
                    dialogTitle="Bulk Upload Products"
                    primaryButtonLabel="Save"
                    secondaryButtonLabel="Cancel"
                    isOpen={isOpen}
                    onSecondaryButtonClick={(e) => {
                        formik.handleReset(e);
                        onClose();
                    }}
                    isPrimaryButtonDisabled={
                        !(products && formik.dirty && formik.isValid) || formik.isSubmitting
                    }
                    right
                    dialogContent={
                        <>
                            <ActionButtonText
                                label="Example Template"
                                className="margin__bottom--medium"
                                leftIcon={IconNames.IMPORT}
                                onClick={() =>
                                    saveAs(
                                        new File(
                                            productListBulkTemplateCSVMap[listSchema],
                                            'product_list_bulk_template.csv',
                                            {
                                                type: 'text/csv;charset=utf-8',
                                            },
                                        ),
                                    )
                                }
                            />
                            {formik.values.validationErrors &&
                                Object.entries(formik.values.validationErrors).map(
                                    ([row, invalidValues]) => (
                                        <div key={row} className="margin__bottom--medium">
                                            <InlineMessage
                                                intent={InlineMessage.Intent.ERROR}
                                                title={`Row ${row}`}
                                                label={invalidValues.map((msg) => (
                                                    <div key={msg}>{msg}</div>
                                                ))}
                                            />
                                        </div>
                                    ),
                                )}
                            <FileUploadFormik
                                id="productFile"
                                label="Product File"
                                required
                                accept=".csv"
                                maxAllowedFiles={1}
                                onAddFiles={(files) =>
                                    formik.setFieldValue('fileToUpload', files?.[0])
                                }
                                onRemoveFile={() => formik.setFieldValue('fileToUpload', undefined)}
                            />
                        </>
                    }
                />
            )}
        </Formik>
    );
};

ProductsBulkUploadDialog.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    listSchema: PropTypes.oneOf(Object.values(ListSchema)).isRequired,
};
